import { Check, FilePen } from 'flowbite-react-icons/outline'

import { sectionCardTitleClass } from './FormCard'

interface AuthorizingClinicianCardProps {
  /** Clinician name displayed on the card. Falls back to "Unknown" when blank. */
  name: string
  /** Clinician NPI. Shown as `NPI #{npi}` when present, otherwise "No NPI on file". */
  npi: string | undefined | null
  /** Optional credential suffix appended after the name (e.g. "MD"). */
  roleTitle?: string
  /** Called when the user clicks "Change Authorizing Clinician". */
  onChangeClick: () => void
  /** Disables the Change button (e.g. while the order is being submitted). */
  disabled?: boolean
  /** Picker UI rendered inside the card when a change is in progress. */
  children?: React.ReactNode
  /**
   * When provided, renders a labelled checkbox below the clinician identity
   * (and below the picker slot when open). The string becomes both the visible
   * label and the accessible name for the checkbox.
   */
  attestationLabel?: string
  /** Controlled checked state for the attestation checkbox. */
  checked?: boolean
  /** Called with the new checked value when the checkbox changes. */
  onCheckedChange?: (checked: boolean) => void
}

/**
 * Card that identifies the current authorizing clinician (signer of record) and
 * provides a single "Change Authorizing Clinician" action that opens the existing
 * Epic search or standalone org-practitioner picker.
 */
export function AuthorizingClinicianCard({
  name,
  npi,
  roleTitle,
  onChangeClick,
  disabled = false,
  children,
  attestationLabel,
  checked = false,
  onCheckedChange,
}: AuthorizingClinicianCardProps) {
  const displayName = name?.trim() || 'Unknown'
  const trimmedNpi = npi?.trim() ?? ''
  const npiText = trimmedNpi ? `NPI #${trimmedNpi}` : 'No NPI on file'

  return (
    <div className="order-card w-full min-w-0 !p-4 sm:!p-7">
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className={sectionCardTitleClass}>
            Authorizing Clinician
          </h3>
          <button
            type="button"
            onClick={onChangeClick}
            disabled={disabled}
            className="inline-flex shrink-0 items-center justify-center gap-1 rounded-full border border-border-base bg-white px-2 py-1 text-[10px] font-medium text-text-body transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 sm:gap-1.5 sm:px-2.5 sm:py-1.5 sm:text-xs"
          >
            <FilePen className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden />
            Change Authorizing Clinician
          </button>
        </div>

        <div className="border border-border-base rounded-base p-3 flex flex-col gap-1">
          <p className="text-base font-bold leading-6 text-text-heading dark:text-white">
            {displayName}
            {roleTitle?.trim() && (
              <span className="font-medium text-text-muted">{`, ${roleTitle.trim()}`}</span>
            )}
          </p>
          <p className="text-base font-medium leading-6 text-text-heading dark:text-gray-200">{npiText}</p>
        </div>

        {children ? <div className="flex flex-col gap-3">{children}</div> : null}

        {attestationLabel?.trim() && (
          // Focus ring lives on the outer chip so the entire pill highlights on keyboard focus —
          // more visible than a ring on the 20px circular indicator alone.
          <label
            className={`flex self-start max-w-full items-center gap-3 rounded-full border border-border-base bg-white px-4 py-2.5 focus-within:ring-2 focus-within:ring-brand-600/60 focus-within:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 ${
              disabled
                ? 'cursor-not-allowed opacity-50'
                : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {/* Circular check indicator — sits over the real checkbox for visual fidelity */}
            <span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onCheckedChange?.(e.target.checked)}
                disabled={disabled}
                className="absolute inset-0 h-full w-full cursor-pointer rounded-full opacity-0 outline-none disabled:cursor-not-allowed"
              />
              <span
                className={`pointer-events-none flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors ${
                  checked
                    ? 'border-gray-400 bg-gray-200 dark:border-gray-500 dark:bg-gray-600'
                    : 'border-gray-300 bg-white dark:border-gray-500 dark:bg-gray-700'
                }`}
                aria-hidden="true"
              >
                {checked && <Check className="h-3 w-3 text-gray-700 dark:text-gray-200" />}
              </span>
            </span>
            <span className="select-none text-sm leading-5 text-text-body dark:text-gray-400">
              {attestationLabel}
            </span>
          </label>
        )}
      </div>
    </div>
  )
}
